import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import useReveal from '../../hooks/useReveal';
import { resolveMediaUrl } from '../admin/MediaUpload';
import { formatMoneyCompact } from '../../lib/format';
import ServiceDetailModal from './ServiceDetailModal';

export default function GameCards({ onEnquire, onBuy }) {
  const { data, walletUserEnabled } = useSite();
  const { matches } = useFilter();
  const cards = (data.gameCards || []).filter(matches);
  const buyEnabled = walletUserEnabled !== false;
  const [openCard, setOpenCard] = useState(null);

  if (cards.length === 0) {
    return <div className="text-zinc-500 text-sm text-center py-8" data-testid="gamecards-empty">No services match your filter.</div>;
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2.5 md:gap-3" id="gamecards-grid" data-testid="gamecards-grid">
        {cards.map((c, idx) => {
          const Icon = Icons[c.icon] || Icons.Gamepad2;
          return (
            <ServiceCard
              key={c.id}
              card={c}
              Icon={Icon}
              delay={idx * 60}
              onClick={() => setOpenCard(c)}
              showPrice={Number(c.price || 0) > 0 && buyEnabled}
            />
          );
        })}
      </div>

      <ServiceDetailModal
        open={!!openCard}
        onOpenChange={(o) => !o && setOpenCard(null)}
        card={openCard}
        buyEnabled={buyEnabled}
        onBuy={() => openCard && onBuy?.('service', { id: openCard.id, name: openCard.label, price: openCard.price, image: openCard.image, gradient: openCard.gradient })}
        onEnquire={() => openCard && onEnquire?.({ id: 'service' }, { name: openCard.label, id: openCard.id })}
      />
    </>
  );
}

function ServiceCard({ card, Icon, delay, onClick, showPrice }) {
  const { ref, visible } = useReveal();
  return (
    <button
      ref={ref}
      id={`service-${card.id}`}
      data-anchor={`service-${card.id}`}
      onClick={onClick}
      type="button"
      style={{ background: card.gradient, transitionDelay: `${delay}ms` }}
      className={`group relative h-24 md:h-28 rounded-lg overflow-hidden text-left transition-all duration-500 ease-out cursor-pointer scroll-mt-32
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
        hover:shadow-[0_8px_24px_-8px_rgba(212,215,122,0.45)] hover:scale-[1.02]`}
      data-testid={`service-card-${card.id}`}
    >
      <div className="absolute inset-0 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.4),transparent_60%)] pointer-events-none" />
      <div className="relative h-full flex items-center px-3 md:px-4 gap-2.5 md:gap-3">
        <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/30 flex items-center justify-center text-white group-hover:bg-black/50 transition overflow-hidden shrink-0">
          {card.image ? <img src={resolveMediaUrl(card.image)} alt="" className="w-full h-full object-cover" /> : <Icon size={16} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm md:text-base drop-shadow-sm truncate">{card.label}</div>
          {card.sub && <div className="text-white/80 text-[10px] md:text-[11px] truncate">{card.sub}</div>}
          {showPrice && (
            <div className="text-[color:var(--gold)] text-[11px] md:text-xs font-extrabold notranslate mt-0.5" translate="no" data-testid={`service-card-price-${card.id}`}>
              {formatMoneyCompact(card.price)}
            </div>
          )}
        </div>
      </div>
      <div className="absolute right-2 -bottom-2 w-16 h-16 rounded-full bg-white/5 blur-md pointer-events-none" />
    </button>
  );
}
